import $ from "../../libs/jquery-3.3.1.min.js"
import {CustomWiggle, CustomEase, gsap} from "../../libs/gsap/all.js";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameE_Tag} from "./components/GameE_Tag";
import {GameE_Dropzone} from "./components/GameE_Dropzone";
import {AppearingInteractiveButton} from "./components/AppearingInteractiveButton";
import {VideoSource} from "pixi.js";

// Game description : Schema troué - drag and drop tags in zones
export class GameE extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;
    this.tagWidth = this.data.tagsSize.width;
    this.tagHeight = this.data.tagsSize.height;

    this.dragTarget = false;
    this.tagsObjects = [];
    this.tagsBackgrounds = [];
    this.dropzoneObjects = [];
    this.isCheckingAnswer = false;
    this.foundAnswers = 0;
    this.droppedCount = 0;

    this.$screenEl = null;

    this.sentenceTextStyle = new PIXI.TextStyle({
      fontFamily: "Sketchnote Text",
      fontSize: 32,
      lineHeight: 42 * 1.24,
      fontWeight: 'normal',
      fill: "#001166",
      align: "left",
      wordWrap: true,
      wordWrapWidth: this.data.imageDimensions.width
    });
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {

    this.$screenEl = $(this.canvas).parents(".screen");

    // Enable events on the whole stage
    this.stage.hitArea = this.screen;

    this.questionTextContainer = this.$screenEl.find(".game-question");
    this.questionTextContainer.css({
      top: this.data.question.y,
      left: this.data.question.x,
    });

    const imageMargin = {x: 70, y: 80};
    const interactiveZoneSize = {width: 1610, height: 770};

    // Prepare image (if any)
    this.imageContainer = new PIXI.Container();

    this.imageContainer.position.x = imageMargin.x;
    this.imageContainer.position.y = imageMargin.y;

    if (this.assets["image"] || this.assets["video"]) {

      // Define texture from video if available, or image otherwise.
      // If a video is given, make it loop
      const texture = this.assets["video"] || this.assets["image"];
      if (texture.source instanceof VideoSource) texture.source.resource.loop = true

      this.imageSprite = new PIXI.Sprite(texture);
      // this.imageSprite.anchor.set(0.5, 0); // center in x easier
      this.imageSprite.width = this.data.imageDimensions.width;
      this.imageSprite.height = this.data.imageDimensions.height;
      this.imageContainer.addChild(this.imageSprite);
    } else if (this.data.sentence) {
      // Prepare text (if any)
      const sentenceTextEN = this.createInterfaceHTMLText(this.data.sentence.text.en, this.sentenceTextStyle, "en");
      const sentenceTextFR = this.createInterfaceHTMLText(this.data.sentence.text.fr, this.sentenceTextStyle, "fr");

      this.imageContainer.addChild(sentenceTextEN);
      this.imageContainer.addChild(sentenceTextFR);

      sentenceTextEN.position.set(0, (sentenceTextEN.height - imageMargin.y) / 2);
      sentenceTextFR.position.set(0, (sentenceTextFR.height - imageMargin.y) / 2);
    }

    this.stage.addChild(this.imageContainer);

    const rightContainerWidth = interactiveZoneSize.width - this.data.imageDimensions.width;
    const rightContainerPosition = {
      x: this.imageContainer.position.x + this.data.imageDimensions.width + 20,
      y: this.imageContainer.position.y
    };

    // Prepare choices
    this.interactiveContainer = new PIXI.Container();
    this.interactiveContainer.sortableChildren = true
    this.interactiveContainer.position.x = this.imageContainer.position.x;
    this.interactiveContainer.position.y = this.imageContainer.position.y;
    // Let's add a background so we have a defined zone with specific size
    let interactiveZoneBackground = new PIXI.Graphics().rect(0, 0, interactiveZoneSize.width, interactiveZoneSize.height).fill({
      color: 0xff0000,
      alpha: 0
    });
    this.interactiveContainer.addChild(interactiveZoneBackground);
    this.stage.addChild(this.interactiveContainer);


    // Add buttons before the tags so the zIndex of the tags work as expected (aka above the buttons)
    // Add validate button
    const checkmark = new PIXI.Sprite(this.assets["checkmark"]);
    const checkmarkDisabled = new PIXI.Sprite(this.assets["checkmark-disabled"]);
    this.validateButton = new AppearingInteractiveButton(75, 75, checkmark, checkmarkDisabled, this.validateAnswers.bind(this), false);
    this.stage.addChild(this.validateButton);

    // Add reset button
    const reset = new PIXI.Sprite(this.assets["reset"]);
    const resetDisabled = new PIXI.Sprite(this.assets["reset-disabled"]);
    this.resetButton = new AppearingInteractiveButton(75, 75, reset, resetDisabled, this.resetAnswers.bind(this), false);
    this.stage.addChild(this.resetButton);

    const baseTagsX = this.data.tags.length <= 4 ? (rightContainerPosition.x + rightContainerWidth / 2) - imageMargin.x : (rightContainerPosition.x + imageMargin.x);
    const baseTagsY = rightContainerPosition.y + 180;

    for (let i = 0; i < this.data.tags.length; i++) {
      const spaceBetween = 25;

      let tagX = 0;
      let tagY = 0;

      if (this.data.tags.length > 4) {
        // make 2 columns
        tagX = baseTagsX + ((i % 2 * this.data.tagsSize.width)) + (i % 2 * spaceBetween);

        tagY = baseTagsY + (Math.floor(i / 2) * this.data.tagsSize.height) + (Math.floor(i / 2) * spaceBetween);
        // tagY = baseTagsY + (i * this.data.tagsSize.height) + (i * spaceBetween);
      } else {
        tagX = baseTagsX;
        tagY = baseTagsY + (i * this.data.tagsSize.height) + (i * spaceBetween);
      }

      let tag = new GameE_Tag(tagX, tagY, this.data.tags[i], this);
      let dropZone = new GameE_Dropzone(this.data.tags[i], this);

      this.interactiveContainer.addChild(tag.getBgArea());
      this.interactiveContainer.addChild(tag);
      this.interactiveContainer.addChild(dropZone);

      this.tagsObjects.push(tag);
      this.tagsBackgrounds.push(tag.bgArea);
      this.dropzoneObjects.push(dropZone);

      tag.on("pointerdown", (e) => {
        this.dragTarget = e.currentTarget; // save the target so we can keep "this" scope
        this.handleOnDragStart(e);
      }).on("pointerup", (e) => {
        this.handleOnDragEnd(e);
      }).on("pointerupoutside", (e) => {
        this.handleOnDragEnd(e);
      });
    }


    // We can now position the buttons
    let lastTag = this.tagsObjects[this.tagsObjects.length - 1];
    this.validateButton.position.set(rightContainerPosition.x + (rightContainerWidth / 2) + 60, (lastTag.position.y + lastTag.height + 40) + (checkmark.height / 2));
    this.resetButton.position.set(rightContainerPosition.x + (rightContainerWidth / 2) - 40, (lastTag.position.y + lastTag.height + 40) + (checkmark.height / 2));

    // Used for debugging in pixijs devtool
    //globalThis.__PIXI_APP__ = this;
  }

  handleOnDragStart(e) {
    // this.dragTarget.alpha = 0.5;
    this.dragTarget.pickedUp();
    const clickPos = this.dragTarget.parent.toLocal(e.global, null);

    // place tag on mouse
    gsap.to(this.dragTarget.position, {
      x: clickPos.x,
      y: clickPos.y,
      duration: 0.05
    });

    this.stage.eventMode = 'static';
    this.stage.on("pointermove", (e) => {
      this.handleOnDrag(e);
    });
  }

  handleOnDrag(e) {
    if (this.dragTarget) {
      const clickPos = this.dragTarget.parent.toLocal(e.global, null);

      // tag follow mouse
      gsap.killTweensOf(this.dragTarget.position);
      gsap.to(this.dragTarget.position, {
        x: clickPos.x,
        y: clickPos.y,
        duration: 0.05
      });
    }
  }

  handleOnDragEnd(e) {
    if (this.dragTarget) {
      this.stage.eventMode = 'auto';
      this.stage.off("pointermove");

      this.dragTarget.released();

      // Check if we landed on the related dropzone or not
      let droppedInZone = this.checkDropZone(e.global);

      if (droppedInZone !== false) {
        this.droppedCount++;

        droppedInZone.isFilled = true;
        this.dragTarget.droppedInZone = droppedInZone;
        this.dragTarget.disable();
        this.dragTarget.animateDropped(droppedInZone);
        droppedInZone.animateDropped();

        if (this.droppedCount > 0) {
          this.resetButton.enable();
        }

        if (this.droppedCount >= this.data.tags.length) {
          this.validateButton.enable();
          return;
        }
      } else {
        // reset position
        this.dragTarget.resetPosition();
        // gsap.killTweensOf(this.dragTarget.position);
        // gsap.to(this.dragTarget.position, {
        //   x: this.dragTarget.originalPosition.x,
        //   y: this.dragTarget.originalPosition.y,
        //   duration: 0.25
        // })
      }

      this.dragTarget = false;
    }
  }

  checkDropZone(cursorPos) {

    // check if we dropped in a zone
    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      if (this.dropzoneObjects[i].isFilled) {
        continue;
      }
      // get the global position of the drop target so we can compare with the cursor position
      let dropZoneBoundingBox = this.dropzoneObjects[i].getBoundingBox();

      if (
        (cursorPos.x >= dropZoneBoundingBox.left) &&
        (cursorPos.x <= dropZoneBoundingBox.right) &&
        (cursorPos.y >= dropZoneBoundingBox.top) &&
        (cursorPos.y <= dropZoneBoundingBox.bottom)
      ) {
        return this.dropzoneObjects[i];
      }
    }

    return false;
  }

  validateAnswers() {
    let goodAnswers = 0;

    for (let i = 0; i < this.tagsObjects.length; i++) {
      if (this.tagsObjects[i].id !== this.tagsObjects[i].droppedInZone.id) {

        // Shake animation before placing back the tag
        gsap.to(this.tagsObjects[i], {
          x: this.tagsObjects[i].position.x + 10,
          duration: 0.50,
          ease: CustomWiggle.create("wrongAnswerWiggle", {
            wiggles: 4,
            type: "easeOut"
          }),
        });

        // Place the tag back
        setTimeout(() => {
          this.tagsObjects[i].droppedInZone.resetState();
          this.tagsObjects[i].resetState();
          this.tagsObjects[i].enable();
        }, 1200);
      } else {
        this.tagsObjects[i].isValid = true;
        goodAnswers++;
      }
    }

    if (goodAnswers >= this.tagsObjects.length) {
      // Everything is good! let's go to step 2
      this.resetButton.hide();
      this.validateButton.hide();
      this.goToConclusion();
    } else {
      this.resetButton.disable();
      this.validateButton.disable();
      this.droppedCount = goodAnswers;
    }
  }

  resetAnswers() {
    let goodAnswers = 0;

    for (let i = 0; i < this.tagsObjects.length; i++) {
      if (!this.tagsObjects[i].isValid) {
        this.tagsObjects[i].resetState();
        this.tagsObjects[i].enable();
      } else {
        goodAnswers++;
      }
    }

    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      this.dropzoneObjects[i].resetState();
    }

    this.droppedCount = goodAnswers;

    this.resetButton.disable();
    this.validateButton.disable();
  }

  buildBegginingTimeline() {
    this.beginningTimeline = gsap.timeline({paused: true});

    this.beginningTimeline.set(this.questionTextContainer, {
      alpha: 1.0
    });

    this.beginningTimeline.set(this.tagsBackgrounds, {
      alpha: 0.0
    });

    this.beginningTimeline.fromTo(this.$screenEl.find('.game-question'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.50,
      immediateRender: true
    });

    this.beginningTimeline.add(() => {
      for (let i = 0; i < this.tagsObjects.length; i++) {
        // stagger the animation
        setTimeout(() => {
          this.tagsObjects[i].animateIn();
          this.dropzoneObjects[i].animateIn();
        }, 200 * i);
      }

      // Show validate button but disabled
      setTimeout(() => {
        this.resetButton.show(false);
      }, this.tagsObjects.length * 200 + 600);

      setTimeout(() => {
        this.validateButton.show(false);
      }, this.tagsObjects.length * 200 + 800);
    });
  }

  buildConclusionTimeline() {

    this.conclusionTimeline = gsap.timeline({paused: true});

    this.conclusionTimeline.addLabel("fade-out-question");

    this.conclusionTimeline.to(this.questionTextContainer, {
      alpha: 0.0,
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut"
    }, "fade-out-question");

    this.conclusionTimeline.to(this.tagsBackgrounds, {
      alpha: 0.0,
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut"
    }, "fade-out-question");

    this.conclusionTimeline.add(() => {
      for (let i = 0; i < this.dropzoneObjects.length; i++) {
        // stagger the animation
        setTimeout(() => {
          this.dropzoneObjects[i].animateAnswer();
        }, 100 * i);
      }
    });

    // force game conclusion to be active
    this.conclusionTimeline.set(this.$screenEl.find(".game-conclusion"), {
      alpha: 1,
    });

    if (this.$screenEl.find(".container-title").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title"), {
        '--title-width': "0%"
      }, {
        '--title-width': "100%",
        duration: 0.45,
        ease: "power2.out",
        immediateRender: true
      });

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title-inner"), {
        alpha: 0.0
      }, {
        alpha: 1.0,
        duration: 0.30,
        ease: "power1.in",
        immediateRender: true
      });
    }

    this.conclusionTimeline.fromTo(this.$screenEl.find('.container-content'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.30,
      immediateRender: true
    });

    this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
      y: "+=100%"
    }, {
      y: 0,
      duration: 1.50,
      ease: "power1.inOut",
      immediateRender: true
    }, "+=0.75");

    this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
      autoAlpha: 0
    }, {
      autoAlpha: 1,
      duration: 0.1,
      ease: "linear",
      immediateRender: true
    }, '<=');

    // Show container button
    if (this.$screenEl.find(".container-button").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-button"), {
        alpha: 0.0,
        scale: 0.0
      }, {
        alpha: 1.0,
        scale: 1.0,
        duration: 0.75,
        ease: "power2.out",
        immediateRender: true,
        onStart: () => {
          // Disable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").removeClass("play-animation");
        },
        onComplete: () => {
          this.$screenEl.find(".game-conclusion").removeClass("disabled");

          // Enable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").addClass("play-animation");
        }
      });

      // Disable click on this section
      this.$screenEl.find(".game-conclusion").addClass("disabled");
    }

  }

  goToConclusion() {
    this.conclusionTimeline.play(0);
  }

  beforeAnimateIn() {
    // rebuild timeline

    if (this.beginningTimeline) {
      this.beginningTimeline.pause();
      // this.conclusionTimeline.progress(0, true);
    } else {
      this.buildBegginingTimeline();
    }

    if (this.conclusionTimeline) {
      this.conclusionTimeline.pause();
      // this.conclusionTimeline.progress(0, true);
    } else {
      this.buildConclusionTimeline();
    }
  }

  begin() {
    this.beginningTimeline.play(0);
  }

  reset() {
    //console.log("gameE Reset");

    this.foundAnswers = 0;
    this.isCheckingAnswer = false;
    this.droppedCount = 0;

    this.resetButton.hide();
    this.validateButton.hide();

    for (let i = 0; i < this.tagsObjects.length; i++) {
      this.tagsObjects[i].reset();
      this.dropzoneObjects[i].reset();
    }

    // reset some states
    // gsap.killTweensOf([this.questionTextContainer, this.tagsBackgrounds]);
    // gsap.set([this.questionTextContainer, this.tagsBackgrounds], {
    //   alpha: 0.0
    // });

    // rebuild timeline
    if (this.beginningTimeline) {
      this.beginningTimeline.kill();
      this.beginningTimeline = null;
    }
    this.buildBegginingTimeline();

    if (this.conclusionTimeline) {
      this.conclusionTimeline.kill();
      this.conclusionTimeline = null;
    }
    this.buildConclusionTimeline();
  }

}
